import { logger } from 'src/logger';
import { RecentsConstants } from './RecentsConstants';

const CFG = RecentsConstants.GLOBAL_SEARCH;

export interface RecentSearchEntry {
  query: string;
  visitedAt: string;
}

interface StoragePayload {
  version: number;
  entries: RecentSearchEntry[];
}

const EMPTY: StoragePayload = { version: CFG.STORAGE_VERSION, entries: [] };

/** Parse localStorage value. Returns empty payload on any failure (self-healing). */
function readStorage(): StoragePayload {
  try {
    const raw = localStorage.getItem(CFG.STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as StoragePayload;
    if (parsed.version !== CFG.STORAGE_VERSION || !Array.isArray(parsed.entries)) {
      writeStorage(EMPTY);
      return EMPTY;
    }
    return parsed;
  } catch {
    writeStorage(EMPTY);
    return EMPTY;
  }
}

function writeStorage(payload: StoragePayload): void {
  try {
    localStorage.setItem(CFG.STORAGE_KEY, JSON.stringify(payload));
  } catch (err: unknown) {
    logger?.warn?.('RecentSearchesService: localStorage write failed', { error: (err as Error)?.message });
  }
}

/** Filter out entries older than MAX_AGE_DAYS. */
function filterExpired(entries: RecentSearchEntry[]): RecentSearchEntry[] {
  const cutoff = Date.now() - CFG.MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
  return entries.filter((e) => new Date(e.visitedAt).getTime() >= cutoff);
}

export class RecentSearchesService {
  static record(query: string): void {
    const trimmed = query.trim();
    if (trimmed.length < CFG.MIN_QUERY_LENGTH || trimmed.length > CFG.MAX_QUERY_LENGTH) return;

    const { entries } = readStorage();
    const deduped = entries.filter((e) => e.query !== trimmed);
    deduped.unshift({ query: trimmed, visitedAt: new Date().toISOString() });
    writeStorage({ version: CFG.STORAGE_VERSION, entries: deduped.slice(0, CFG.CAP) });
  }

  static getAll(): RecentSearchEntry[] {
    const { entries } = readStorage();
    return filterExpired(entries).slice(0, CFG.CAP);
  }

  static remove(query: string): void {
    const { entries } = readStorage();
    writeStorage({ version: CFG.STORAGE_VERSION, entries: entries.filter((e) => e.query !== query) });
  }

  static clearAll(): void {
    writeStorage(EMPTY);
  }
}
